import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../context/AuthContext';
import LanguageContext from '../../context/LanguageContext';
import AccessibilityContext from '../../context/AccessibilityContext';

const AppSettings = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { language, changeLanguage } = useContext(LanguageContext);
  const { fontSize, highContrast, reducedMotion, updateAccessibilitySettings } = useContext(AccessibilityContext);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    // Language settings
    language: language || 'en',
    
    // Accessibility options
    fontSize: fontSize,
    highContrast: highContrast,
    reducedMotion: reducedMotion
  });
  
  // Update settings when language changes
  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      language: language || 'en'
    }));
  }, [language]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setSettings({
      ...settings,
      [name]: newValue
    });

    // If language is changed, update the LanguageContext
    if (name === 'language') {
      changeLanguage(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Update language setting
    changeLanguage(settings.language);
    
    // Update accessibility settings
    updateAccessibilitySettings({
      fontSize: settings.fontSize,
      highContrast: settings.highContrast,
      reducedMotion: settings.reducedMotion
    });
    
    // Show success message
    setSuccess('Settings updated successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '40px auto',
      padding: '30px',
      backgroundColor: 'var(--card-background)',
      borderRadius: '8px',
      boxShadow: '0 2px 10px var(--shadow-color)'
    },
    header: {
      borderBottom: '2px solid var(--secondary-color)',
      paddingBottom: '15px',
      marginBottom: '25px'
    },
    title: {
      color: 'var(--text-color)',
      fontSize: '24px',
      fontWeight: 'bold'
    },
    section: {
      marginBottom: '30px'
    },
    sectionTitle: {
      color: 'var(--text-color)',
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '15px',
      paddingBottom: '5px',
      borderBottom: '1px solid var(--secondary-color)'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      color: 'var(--text-color)',
      fontWeight: 'bold'
    },
    input: {
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid var(--input-border)',
      fontSize: '16px',
      width: '100%',
      backgroundColor: 'var(--input-background)',
      color: 'var(--text-color)'
    },
    select: {
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid var(--input-border)',
      fontSize: '16px',
      backgroundColor: 'var(--input-background)',
      color: 'var(--text-color)',
      width: '100%'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    checkbox: {
      marginRight: '10px'
    },
    buttonContainer: {
      marginTop: '30px',
      display: 'flex',
      justifyContent: 'flex-end'
    },
    button: {
      backgroundColor: 'var(--primary-color)',
      color: 'var(--header-text)',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.3s ease'
    },
    success: {
      backgroundColor: 'var(--success-color)',
      color: 'white',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    error: {
      backgroundColor: 'var(--danger-color)',
      color: 'white',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>{t('settings.appearance')}</h2>
      </div>

      {success && <div style={styles.success}>{success}</div>}
      {error && <div style={styles.error}>{error}</div>}

      <form onSubmit={handleSubmit}>


        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t('settings.languageRegion')}</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>{t('settings.language')}</label>
            <select 
              name="language" 
              value={settings.language} 
              onChange={handleChange}
              style={styles.select}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>
        
        {/* Data Display Preferences section removed */}
        
        {/* Report Generation Settings section removed */}
        
        {/* Dashboard Customization section removed */}
        
        {/* Template Library Defaults section removed */}
        
        {/* Accessibility Options */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Accessibility Options</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Font Size</label>
            <select 
              name="fontSize" 
              value={settings.fontSize} 
              onChange={handleChange}
              style={styles.select}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="xlarge">Extra Large</option>
            </select>
          </div>
          
          <div style={styles.formGroup}>
            <div style={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="highContrast"
                name="highContrast"
                checked={settings.highContrast}
                onChange={handleChange}
                style={styles.checkbox}
              />
              <label htmlFor="highContrast" style={{...styles.label, margin: 0}}>
                High Contrast Mode
              </label>
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <div style={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="reducedMotion"
                name="reducedMotion"
                checked={settings.reducedMotion}
                onChange={handleChange}
                style={styles.checkbox}
              />
              <label htmlFor="reducedMotion" style={{...styles.label, margin: 0}}>
                Reduced Motion
              </label>
            </div>
          </div>
        </div>
        
        {/* Export/Import Preferences section removed */}

        <button type="submit" style={styles.button}>{t('settings.saveChanges')}</button>
      </form>
    </div>
  );
};

export default AppSettings;
